import { useState, useEffect, useCallback, Fragment } from 'react'
import toastService from '@/services/toast/service'
import { AlertCircle, Loader2, Save, Pencil } from 'lucide-react'
import {
  xrayAdminService,
  type AdminXrayPlan,
  type AdminRoasPlanBonus,
  type AdminXrayPricingGrid,
} from '@/services/xrayAdmin'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Alert, AlertDescription } from '@/components/ui/alert'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table'
import { formatMoney, centsToInput, parseDollarsToCents } from '@/lib/xray-pricing'
import { XraySettingsSkeleton } from '@/components/skeletons'
import XrayPlanEditModal from './XrayPlanEditModal'
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip'
import { useIsViewer, VIEWER_NO_PERMISSION } from '@/hooks/useIsViewer'

// =========================================================================
// Page
// =========================================================================

export default function XraySettings() {
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  const [roasPlans, setRoasPlans] = useState<AdminRoasPlanBonus[]>([])
  const [xrayPlans, setXrayPlans] = useState<AdminXrayPlan[]>([])
  const [grid, setGrid] = useState<AdminXrayPricingGrid | null>(null)
  const [gridError, setGridError] = useState<string | null>(null)

  const reload = useCallback(async () => {
    setLoading(true)
    setError(null)
    setGridError(null)
    const [main, gridResult] = await Promise.allSettled([
      Promise.all([xrayAdminService.listRoasBonus(), xrayAdminService.listPlans()]),
      xrayAdminService.getPricingGrid(),
    ])

    if (main.status === 'fulfilled') {
      const [r, p] = main.value
      setRoasPlans(r)
      setXrayPlans(p)
    } else {
      setError(main.reason?.message || 'Failed to load X-Ray settings')
    }

    if (gridResult.status === 'fulfilled') {
      setGrid(gridResult.value)
    } else {
      setGridError("Couldn't load the pricing grid")
    }

    setLoading(false)
  }, [])

  useEffect(() => {
    void reload()
  }, [reload])

  if (loading) return <XraySettingsSkeleton />

  return (
    <div className="space-y-4 sm:space-y-6 w-full max-w-[1310px] mx-auto px-4 sm:px-0 animate-in fade-in duration-300">
      <div>
        <h1 className="text-xl sm:text-2xl font-semibold tracking-tight">X-Ray Settings</h1>
        <p className="text-sm text-muted-foreground mt-1">
          Configure X-Ray plans and their monthly wallet deposits, the per-lead price of each
          product, and the one-time welcome funds a ROASForm plan includes.
        </p>
      </div>

      {error && (
        <Alert variant="destructive">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}

      <Tabs defaultValue="pricing" className="w-full">
        <TabsList>
          <TabsTrigger value="pricing">Pricing</TabsTrigger>
          <TabsTrigger value="plans">X-Ray Plans</TabsTrigger>
          <TabsTrigger value="roas">ROASForm Welcome Funds</TabsTrigger>
        </TabsList>

        <TabsContent value="pricing" className="mt-4">
          <PricingTab grid={grid} setGrid={setGrid} error={gridError} />
        </TabsContent>

        <TabsContent value="plans" className="mt-4">
          <XrayPlansTab
            plans={xrayPlans}
            onPlanUpdated={(p) => {
              setXrayPlans((prev) => prev.map((x) => (x.id === p.id ? p : x)))
              // The grid header shows each plan's fee, so keep it in step.
              setGrid((prev) =>
                prev
                  ? {
                      ...prev,
                      plans: prev.plans.map((g) =>
                        g.id === p.id
                          ? {
                              ...g,
                              displayName: p.displayName,
                              monthlyFeeCents: p.monthlyFeeCents,
                              includedEnrichUnits: p.includedEnrichUnits,
                            }
                          : g,
                      ),
                    }
                  : prev,
              )
            }}
          />
        </TabsContent>

        <TabsContent value="roas" className="mt-4">
          <RoasWelcomeFundsTab roasPlans={roasPlans} onUpdated={setRoasPlans} />
        </TabsContent>
      </Tabs>
    </div>
  )
}

// =========================================================================
// Tab: Pricing (products x plans)
//
// Rows are products, columns are plans, every cell is that product's per-lead
// price on that plan, in cents. An EMPTY cell means the product is not priced
// on that plan: the charge path fails closed and skips it, which is different
// from a price of 0 (genuinely free). Edits batch into one save.
// =========================================================================

function PricingTab({
  grid,
  setGrid,
  error,
}: {
  grid: AdminXrayPricingGrid | null
  setGrid: React.Dispatch<React.SetStateAction<AdminXrayPricingGrid | null>>
  error: string | null
}) {
  const isViewer = useIsViewer()
  // Keyed "planId:productId" -> the dollar string in that input.
  const [draft, setDraft] = useState<Record<string, string>>({})
  const [saving, setSaving] = useState(false)

  const cellKey = (planId: number, productId: number) => `${planId}:${productId}`

  useEffect(() => {
    if (!grid) return
    setDraft(
      Object.fromEntries(
        grid.prices.map((p) => [cellKey(p.planId, p.productId), centsToInput(p.priceCents)]),
      ),
    )
  }, [grid])

  if (error) {
    return (
      <Card className="py-0 gap-0">
        <CardContent className="pt-6 pb-6">
          <Alert variant="destructive">
            <AlertCircle className="h-4 w-4" />
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        </CardContent>
      </Card>
    )
  }
  if (!grid) return null

  const savedCents = (planId: number, productId: number): number | null =>
    grid.prices.find((p) => p.planId === planId && p.productId === productId)?.priceCents ?? null

  // Every cell whose typed value parses AND differs from what is stored.
  const changes = grid.plans.flatMap((plan) =>
    grid.products.flatMap((product) => {
      const key = cellKey(plan.id, product.id)
      const raw = draft[key]
      if (raw === undefined || raw.trim() === '') return []
      const cents = parseDollarsToCents(raw)
      if (cents == null || cents < 0) return []
      if (cents === savedCents(plan.id, product.id)) return []
      return [{ planId: plan.id, productId: product.id, priceCents: cents }]
    }),
  )

  const anyInvalid = Object.entries(draft).some(([, raw]) => {
    if (raw.trim() === '') return false
    const cents = parseDollarsToCents(raw)
    return cents == null || cents < 0
  })

  const handleSave = async () => {
    if (changes.length === 0) return
    setSaving(true)
    try {
      const saved = await xrayAdminService.updatePrices(changes)
      setGrid((prev) => {
        if (!prev) return prev
        const next = [...prev.prices]
        for (const row of saved) {
          const i = next.findIndex((p) => p.planId === row.planId && p.productId === row.productId)
          if (i === -1) next.push(row)
          else next[i] = row
        }
        return { ...prev, prices: next }
      })
      toastService.success(`${saved.length} price${saved.length === 1 ? '' : 's'} saved`)
    } catch (e: any) {
      toastService.error(e?.response?.data?.message || "Couldn't save prices")
    } finally {
      setSaving(false)
    }
  }

  return (
    <Card className="py-0 gap-0">
      <CardHeader className="py-6 gap-0 border-b">
        <CardTitle className="text-lg font-semibold">Product Pricing</CardTitle>
        <CardDescription>
          What one matched lead costs on each plan. Customers see these numbers directly.
        </CardDescription>
      </CardHeader>
      <CardContent className="pt-6 pb-6">
        <div className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="w-[260px]">Product</TableHead>
                {grid.plans.map((plan) => (
                  <TableHead key={plan.id} className="text-right">
                    <div className="flex flex-col items-end gap-0.5 py-2">
                      <span className="text-sm font-semibold text-foreground">{plan.displayName}</span>
                      <span className="text-xs text-muted-foreground tabular-nums">
                        {formatMoney(plan.monthlyFeeCents)}/mo · {plan.includedEnrichUnits.toLocaleString()} free
                      </span>
                    </div>
                  </TableHead>
                ))}
              </TableRow>
            </TableHeader>
            <TableBody>
              {grid.products.map((product) => (
                <TableRow key={product.id}>
                  <TableCell className="align-middle">
                    <div className="text-sm font-semibold py-2">{product.name}</div>
                  </TableCell>
                  {grid.plans.map((plan) => {
                    const key = cellKey(plan.id, product.id)
                    const raw = draft[key] ?? ''
                    const cents = raw.trim() === '' ? null : parseDollarsToCents(raw)
                    const invalid = raw.trim() !== '' && (cents == null || cents < 0)
                    const saved = savedCents(plan.id, product.id)
                    const isDirty = cents != null && cents !== saved
                    return (
                      <TableCell key={plan.id} className="align-middle py-3">
                        <div className="flex flex-col items-end gap-1">
                          <div className="relative w-28">
                            <span className="pointer-events-none absolute left-2.5 top-1/2 -translate-y-1/2 text-sm text-muted-foreground">
                              $
                            </span>
                            <Input
                              inputMode="decimal"
                              className={`pl-6 text-right tabular-nums ${
                                isDirty ? 'border-foreground/40 ring-1 ring-foreground/10' : ''
                              }`}
                              value={raw}
                              disabled={saving || isViewer}
                              aria-invalid={invalid}
                              placeholder="Not priced"
                              onChange={(e) =>
                                setDraft((prev) => ({
                                  ...prev,
                                  [key]: e.target.value.replace(/[^0-9.]/g, '').replace(/(\..*)\./g, '$1'),
                                }))
                              }
                            />
                          </div>
                          {raw.trim() === '' && saved == null && (
                            <span className="text-[11px] text-muted-foreground">Will not run</span>
                          )}
                        </div>
                      </TableCell>
                    )
                  })}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>

        <div className="mt-6 flex items-center justify-between gap-3 border-t pt-4">
          <p className="text-xs text-muted-foreground max-w-[640px]">
            {changes.length > 0 ? (
              <>
                {changes.length} unsaved change{changes.length === 1 ? '' : 's'}. Click{' '}
                <strong>Save prices</strong> to apply.
              </>
            ) : (
              <>
                An empty cell means the product is not priced on that plan, so it is skipped and
                never charged. A price of 0 is different: the product runs and costs nothing.
              </>
            )}
          </p>
          <Tooltip>
            <TooltipTrigger asChild>
              <span tabIndex={0}>
                <Button
                  onClick={handleSave}
                  disabled={saving || isViewer || anyInvalid || changes.length === 0}
                >
                  {saving ? (
                    <>
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      Saving...
                    </>
                  ) : (
                    <>
                      <Save className="mr-1 h-4 w-4" />
                      Save prices
                    </>
                  )}
                </Button>
              </span>
            </TooltipTrigger>
            {isViewer && <TooltipContent>{VIEWER_NO_PERMISSION}</TooltipContent>}
          </Tooltip>
        </div>
      </CardContent>
    </Card>
  )
}

// =========================================================================
// Tab: X-Ray Plans (card grid + edit modal)
// =========================================================================

function XrayPlansTab({
  plans,
  onPlanUpdated,
}: {
  plans: AdminXrayPlan[]
  onPlanUpdated: (plan: AdminXrayPlan) => void
}) {
  const [editing, setEditing] = useState<AdminXrayPlan | null>(null)
  const isViewer = useIsViewer()

  return (
    <Card className="py-0 gap-0">
      <CardHeader className="py-6 gap-0 border-b">
        <CardTitle className="text-lg font-semibold">X-Ray Plans</CardTitle>
        <CardDescription>How customers see their pricing options.</CardDescription>
      </CardHeader>
      <CardContent className="pt-6 pb-6">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {plans.map((plan) => (
            <div key={plan.id} className="flex flex-col h-full rounded-xl border bg-card p-5">
              <div className="text-[15px] font-bold mb-3">{plan.displayName}</div>
              <div className="mb-5">
                <span className="flex items-baseline gap-1">
                  <span className="text-[28px] font-extrabold tracking-tight">
                    ${plan.displayPrice ?? Math.round(plan.monthlyFeeCents / 100)}
                  </span>
                  <span className="text-muted-foreground/60 text-sm font-medium">/mo</span>
                </span>
              </div>

              <dl className="flex flex-col gap-2 text-[13px] flex-1 mb-5">
                <div className="flex justify-between">
                  <dt className="text-muted-foreground">Free Enriches / mo</dt>
                  <dd className="font-semibold tabular-nums">
                    {plan.includedEnrichUnits.toLocaleString()}
                  </dd>
                </div>
                <div className="flex justify-between">
                  <dt className="text-muted-foreground">Stripe price</dt>
                  <dd className="font-medium truncate max-w-[140px]" title={plan.stripePriceId ?? undefined}>
                    {plan.stripePriceId ?? <span className="text-muted-foreground">Not set</span>}
                  </dd>
                </div>
              </dl>

              <Tooltip>
                <TooltipTrigger asChild>
                  <span tabIndex={0} className="w-full">
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      className="w-full"
                      disabled={isViewer}
                      onClick={() => setEditing(plan)}
                    >
                      <Pencil className="mr-1 h-3.5 w-3.5" />
                      Edit plan
                    </Button>
                  </span>
                </TooltipTrigger>
                {isViewer && <TooltipContent>{VIEWER_NO_PERMISSION}</TooltipContent>}
              </Tooltip>
            </div>
          ))}
        </div>

        <p className="text-xs text-muted-foreground mt-4">
          The monthly price lives in Stripe, and the wallet deposit is taken from it when you save
          the price here. To change what a plan costs, create a new price in Stripe and paste its
          ID. Existing subscribers keep billing on their old price until they re-subscribe.
        </p>
      </CardContent>

      <XrayPlanEditModal
        open={editing !== null}
        onOpenChange={(open) => {
          if (!open) setEditing(null)
        }}
        plan={editing}
        onSaved={(p) => {
          onPlanUpdated(p)
          setEditing(null)
        }}
      />
    </Card>
  )
}

// =========================================================================
// Tab: ROASForm plan welcome funds
// =========================================================================

function RoasWelcomeFundsTab({
  roasPlans,
  onUpdated,
}: {
  roasPlans: AdminRoasPlanBonus[]
  onUpdated: (next: AdminRoasPlanBonus[]) => void
}) {
  const isViewer = useIsViewer()
  const [draft, setDraft] = useState<Record<number, string>>(() =>
    Object.fromEntries(roasPlans.map((p) => [p.id, centsToInput(p.freeXrayCents)])),
  )
  const [saving, setSaving] = useState(false)

  useEffect(() => {
    setDraft(Object.fromEntries(roasPlans.map((p) => [p.id, centsToInput(p.freeXrayCents)])))
  }, [roasPlans])

  const parsed = (plan: AdminRoasPlanBonus): number | null => {
    const raw = draft[plan.id]
    if (raw === undefined) return plan.freeXrayCents
    return parseDollarsToCents(raw)
  }

  // Annual plans carry the same grant as their monthly twin, so they read as
  // duplicates in a flat list. Split them.
  const monthlyPlans = roasPlans.filter((p) => !p.name.endsWith('_yearly'))
  const annualPlans = roasPlans.filter((p) => p.name.endsWith('_yearly'))
  const groupedPlans = [
    { label: 'Monthly', plans: monthlyPlans },
    { label: 'Annual', plans: annualPlans },
  ].filter((g) => g.plans.length > 0)

  const anyInvalid = roasPlans.some((p) => {
    const cents = parsed(p)
    return cents == null || cents < 0
  })
  const dirty = roasPlans.some((p) => {
    const cents = parsed(p)
    return cents != null && cents !== p.freeXrayCents
  })

  const handleSave = async () => {
    if (anyInvalid) return
    const updates = roasPlans.map((p) => ({ id: p.id, freeXrayCents: parsed(p) ?? p.freeXrayCents }))
    setSaving(true)
    try {
      const fresh = await xrayAdminService.updateRoasBonus(updates)
      onUpdated(fresh)
      toastService.success('Welcome funds updated')
    } catch (e: any) {
      toastService.error(e?.response?.data?.message || "Couldn't update welcome funds")
    } finally {
      setSaving(false)
    }
  }

  return (
    <Card className="py-0 gap-0">
      <CardHeader className="py-6 gap-0 border-b">
        <CardTitle className="text-lg font-semibold">ROASForm Plan Welcome Funds</CardTitle>
        <CardDescription>
          Added to an agency's X-Ray wallet once, the first time it subscribes to a paid X-Ray plan.
        </CardDescription>
      </CardHeader>
      <CardContent className="pt-6 pb-6">
        {/* Grouped the way the plans are actually sold: the annual rows repeat
            the monthly grants, and reading them as one flat list of seven made
            that look like seven separate decisions. */}
        <div className="overflow-hidden rounded-lg border">
          <Table>
            <TableHeader>
              <TableRow className="hover:bg-transparent">
                <TableHead className="min-w-[180px] text-[11px] font-medium uppercase tracking-wider text-muted-foreground">
                  ROASForm plan
                </TableHead>
                <TableHead className="w-[200px] text-right text-[11px] font-medium uppercase tracking-wider text-muted-foreground">
                  Welcome funds
                </TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {groupedPlans.map((group) => (
                <Fragment key={group.label}>
                  <TableRow className="border-y bg-muted/50 hover:bg-muted/50">
                    <TableCell
                      colSpan={2}
                      className="py-1.5 text-[11px] font-semibold uppercase tracking-wider text-muted-foreground"
                    >
                      {group.label}
                    </TableCell>
                  </TableRow>
                  {group.plans.map((plan) => {
                    const raw = draft[plan.id] ?? centsToInput(plan.freeXrayCents)
                    const cents = parseDollarsToCents(raw)
                    const invalid = cents == null || cents < 0
                    const isDirty = !invalid && cents !== plan.freeXrayCents
                    return (
                      <TableRow key={plan.id}>
                        <TableCell className="py-2 text-sm font-medium">{plan.displayName}</TableCell>
                        <TableCell className="py-2">
                          <div className="relative ml-auto w-32">
                            <span className="pointer-events-none absolute left-2.5 top-1/2 -translate-y-1/2 text-sm text-muted-foreground">
                              $
                            </span>
                            <Input
                              inputMode="decimal"
                              disabled={saving || isViewer}
                              aria-invalid={invalid}
                              value={raw}
                              onChange={(e) =>
                                setDraft((prev) => ({
                                  ...prev,
                                  [plan.id]: e.target.value.replace(/[^0-9.]/g, '').replace(/(\..*)\./g, '$1'),
                                }))
                              }
                              className={`pl-6 text-right tabular-nums ${
                                isDirty ? 'border-foreground/40 ring-1 ring-foreground/10' : ''
                              }`}
                            />
                          </div>
                        </TableCell>
                      </TableRow>
                    )
                  })}
                </Fragment>
              ))}
            </TableBody>
          </Table>
        </div>
        <div className="mt-6 flex items-center justify-between gap-3 border-t pt-4">
          <p className="text-xs text-muted-foreground">
            {dirty ? (
              <>
                You have unsaved changes. Click <strong>Save welcome funds</strong> to apply.
              </>
            ) : (
              <>Granted one time, and never refreshed. Set 0 for no welcome funds.</>
            )}
          </p>
          <Tooltip>
            <TooltipTrigger asChild>
              <span tabIndex={0}>
                <Button onClick={handleSave} disabled={saving || !dirty || anyInvalid || isViewer}>
                  {saving ? (
                    <>
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      Saving...
                    </>
                  ) : (
                    <>
                      <Save className="mr-1 h-4 w-4" />
                      Save welcome funds
                    </>
                  )}
                </Button>
              </span>
            </TooltipTrigger>
            {isViewer && <TooltipContent>{VIEWER_NO_PERMISSION}</TooltipContent>}
          </Tooltip>
        </div>
      </CardContent>
    </Card>
  )
}
